import { useEffect, useState } from "react";
import { ChevronDown, Calculator } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RentCollection } from "@/components/RentCollection";

const TABS = ["Buying", "Renting", "Mortgage", "Profit", "Rent Collection"];
const FIELDS = {
  Buying: [["purchase_price", "Purchase price (£)"], ["market_value", "Estimated market value (£)"], ["deposit", "Deposit (£)"]],
  Renting: [["monthly_rent", "Monthly rent (£)"], ["management_monthly", "Management fees /mo (£)"], ["insurance_monthly", "Insurance /mo (£)"], ["maintenance_monthly", "Maintenance /mo (£)"], ["other_expenses_monthly", "Other expenses /mo (£)"]],
  Mortgage: [["loan_amount", "Mortgage amount (£)"], ["interest_rate", "Interest rate (%)"], ["term_years", "Term (years)"]],
};

export function FinancialTools({ propertyId = "default" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("Buying");
  const [inputs, setInputs] = useState({});
  const [computed, setComputed] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/property-financials/${propertyId}`).then((r) => {
      setInputs(r.data.user_inputs || {});
      setComputed(r.data.computed);
    }).catch(() => {});
  }, [open, propertyId]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(inputs).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.put(`/property-financials/${propertyId}`, clean);
      setComputed(data.computed);
      toast.success("Financials recalculated");
      setTab("Profit");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Check your numbers");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card" data-testid="financial-tools">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between p-5 text-left" data-testid="financial-tools-toggle">
        <span className="flex items-center gap-2 font-display text-lg"><Calculator className="h-4 w-4 text-primary" /> Financial Tools</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-border p-5">
          <div className="flex flex-wrap gap-1.5">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`fin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-3 py-1 text-xs font-medium ${tab === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>
                {t}
              </button>
            ))}
          </div>
          {tab === "Rent Collection" ? (
            <RentCollection propertyId={propertyId} />
          ) : tab !== "Profit" ? (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FIELDS[tab].map(([key, label]) => (
                <label key={key} className="text-xs text-muted-foreground">
                  {label}
                  <input type="number" value={inputs[key] ?? ""} data-testid={`fin-input-${key}`}
                    onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
                </label>
              ))}
              {tab === "Mortgage" && (
                <label className="text-xs text-muted-foreground">Mortgage type
                  <select value={inputs.mortgage_type || "repayment"} data-testid="fin-input-mortgage_type"
                    onChange={(e) => setInputs({ ...inputs, mortgage_type: e.target.value })}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                    <option value="repayment">Repayment</option>
                    <option value="interest_only">Interest only</option>
                  </select>
                </label>
              )}
            </div>
          ) : computed ? (
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[["Mortgage /mo", computed.monthly_mortgage_payment], ["Rent /mo", computed.gross_monthly_rent],
                  ["Expenses /mo", computed.total_monthly_expenses], ["Net cash flow /mo", computed.net_monthly_cash_flow],
                  ["Annual projected profit", computed.annual_projected_profit],
                  ["Gross yield", computed.gross_rental_yield_pct != null ? `${computed.gross_rental_yield_pct}%` : "—"]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border bg-background p-3 text-center">
                    <div className={`font-display text-lg ${l.includes("cash flow") && v < 0 ? "text-red-400" : ""}`}>{typeof v === "number" ? `£${v.toLocaleString()}` : v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
              <ul className="mt-3 list-disc pl-5 text-[11px] text-muted-foreground">
                {computed.assumptions?.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Enter your numbers in Buying / Renting / Mortgage, then calculate.</p>
          )}
          {tab !== "Rent Collection" && (
            <Button onClick={save} disabled={saving} size="sm" className="mt-4" data-testid="fin-calculate-btn">
              {saving ? "Calculating…" : "Calculate projections"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
